import { FlashList } from '@shopify/flash-list';
import { format, subYears } from 'date-fns';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dimensions, Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Payout } from '@/api';
import { useDashboardMetrics } from '@/api/partners/dashboard/use-dashboard-metrics';
import { usePayouts } from '@/api/partners/payouts/use-payouts';
import { NoCarsEmptyState } from '@/components/first-car-empty-state';
import { useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import {
  capitalizeFirstLetter,
  formatInUTC,
  formatNumber,
  isDesktop,
  toUTCEndOfDayISO,
  toUTCStartOfDayISO,
} from '@/core/utils';
import { Card, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { Chip, ChipType } from '@/ui/chip';
import DataTable from '@/ui/data-table';
import { Icon } from '@/ui/icon';
import { Dollar } from '@/ui/icons/dollar';
import { Dot } from '@/ui/icons/dot';
import { Loader } from '@/ui/loader';

const PAGE_HEIGHT = Dimensions.get('window').height;

export default function Payouts() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDatetime, setStartDatetime] = useState<Date | null>(null);
  const [endDatetime, setEndDatetime] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const { data: dashboardMetricsData, isLoading: isLoadingDashboardMetrics } =
    useDashboardMetrics({});

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isPending,
    isLoading,
    hasNextPage,
    refetch: refetchPayouts,
    isSuccess,
  } = usePayouts({
    enabled: status === 'signIn',
    startDatetime: toUTCStartOfDayISO(startDatetime),
    endDatetime: toUTCEndOfDayISO(endDatetime),
    text: '',
  });

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Payouts',
    });
  }, []);

  const payouts = useMemo(() => {
    return data?.pages.map((page) => page.data).flat();
  }, [data]);
  const hasPayouts = (payouts?.length ?? 0) > 0;
  const hasActiveDateFilter = startDatetime !== null || endDatetime !== null;
  const hasNoCars =
    !isLoadingDashboardMetrics &&
    (dashboardMetricsData?.totalCarsCount ?? 0) === 0;

  useEffect(() => {
    setPage(0);
  }, [startDatetime, endDatetime]);

  const rows = useCallback(
    (payoutsData: Payout[]) => {
      if (!payoutsData) return [];

      return payoutsData?.map((payout) => {
        return {
          key: `payout-${payout.id}`,
          id: payout.id,
          'payout-period': (
            <Pressable
              onPress={() => {
                router.push(`/partner/payouts/${payout.id}`);
              }}
            >
              <Text
                type="bodyRegular"
                style={{ textDecorationLine: 'underline' }}
              >
                {`${formatInUTC(
                  payout.startTime,
                  'MMM d, yyyy'
                )} - ${formatInUTC(payout.endTime, 'MMM d, yyyy')}`}
              </Text>
            </Pressable>
          ),
          'net-earnings': (
            <Text type="bodyRegular" style={{ color: theme.colors.success }}>
              ${formatNumber(payout.earningsMinusExpenses)}
            </Text>
          ),
          status: payout.paymentStatus ? (
            <Chip
              type={
                payout.paymentStatus === 'paid'
                  ? ChipType.SUCCESS
                  : payout.paymentStatus === 'partial_paid'
                  ? ChipType.WARNING
                  : payout.paymentStatus === 'failed'
                  ? ChipType.ERROR
                  : payout.paymentStatus === 'unpaid'
                  ? ChipType.DEFAULT
                  : ChipType.PENDING
              }
              icon={<Dot />}
              text={
                payout.paymentStatus === 'paid' && payout.paymentDate
                  ? `Paid on ${format(
                      new Date(payout.paymentDate),
                      'MMMM d, yyyy'
                    )}`
                  : payout.paymentStatus === 'partial_paid'
                  ? 'Partial paid'
                  : capitalizeFirstLetter(payout.paymentStatus)
              }
              iconWidth={8}
              iconHeight={8}
            />
          ) : null,
        };
      });
    },
    [payouts]
  );

  if (isPending && !data) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (hasNoCars) {
    return (
      <NoCarsEmptyState
        title="No Payouts Yet"
        body="Your first payout will appear here once you have earnings within a payment period"
        ctaText="Add Your First Car"
        redirectPath="partner/cars/setup?fromSf=1"
      />
    );
  }

  if (
    isSuccess &&
    !hasPayouts &&
    !hasActiveDateFilter &&
    (payouts?.length ?? 0) === 0
  ) {
    return (
      <View
        style={{
          backgroundColor: theme.colors.background_1,
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <View style={{ padding: 16, alignItems: 'center', gap: 16 }}>
          <Icon
            type="dressed"
            icon={<Dollar />}
            fillColor={theme.colors.icon.color}
            width={16}
            height={16}
            style={{
              borderRadius: 28,
            }}
          />

          <View style={{ alignItems: 'center', gap: 8 }}>
            <Text type="title2">No Payouts Yet</Text>

            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              Your first payout will appear here once you have earnings within a
              payment period.
            </Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        maxHeight: PAGE_HEIGHT,
      }}
    >
      <View style={styles.container}>
        <View style={styles.nav}>
          <Text type="title1">Payouts</Text>
        </View>

        <View style={{ gap: 24, flex: 1 }}>
          <View style={{ gap: 16, zIndex: 1 }}>
            <View style={{ zIndex: 1 }}>
              <View style={{ width: 115 }}>
                <Card
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingLeft: 16,
                    paddingRight: 16,
                    borderColor:
                      startDatetime && endDatetime
                        ? theme.colors.buttonFilter.active.border
                        : 'transparent',
                  }}
                >
                  <Pressable
                    onPress={() => {
                      setShowCalendar(true);
                    }}
                  >
                    <Text type="bodyRegular">Date range</Text>
                  </Pressable>
                </Card>
              </View>

              <Calendar
                mode="date"
                isVisible={showCalendar}
                headerTitle={'Date (UTC)'}
                minDate={subYears(new Date(), 5)}
                defaultStartDate={startDatetime}
                defaultEndDate={endDatetime}
                onHide={() => {
                  setShowCalendar(false);
                }}
                onClear={() => {
                  setStartDatetime(null);
                  setEndDatetime(null);
                  setShowCalendar(false);
                }}
                onDatesSelected={(startDt, endDt) => {
                  setStartDatetime(startDt);
                  setEndDatetime(endDt);
                  setShowCalendar(false);
                }}
                range
                style={{
                  zIndex: 1,
                  position: 'absolute',
                  top: 0,
                }}
                containerStyle={{
                  borderWidth: 1,
                  borderColor: theme.colors.card.default.borderColor,
                  shadowColor: theme.colors.card.default.borderColor,
                  shadowOffset: {
                    width: 0,
                    height: 5,
                  },
                  shadowOpacity: 0.5,
                  shadowRadius: 5,
                  elevation: 10,
                  height: '100%',
                }}
                id="date-calendar"
                hasBackdrop
                coverScreen
                showDesktopOverview
              />
            </View>

            <View>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Payouts are scheduled every two weeks. Date range filter uses
                UTC.
              </Text>
            </View>
          </View>

          <View style={styles.payoutsContainer}>
            {isDesktop() ? (
              isFetchingNextPage ? (
                <View
                  style={{
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.colors.background_1,
                  }}
                >
                  <Loader style={{ height: 250, width: 250 }} />
                </View>
              ) : (
                <DataTable
                  columns={[
                    {
                      key: 'payout-period',
                      title: 'PAYOUT PERIOD',
                      style: { minWidth: 180 },
                    },
                    {
                      key: 'net-earnings',
                      title: 'NET EARNINGS',
                      numeric: true,
                      style: { width: 160, flex: 0 },
                    },
                    {
                      key: 'status',
                      title: 'STATUS',
                      numeric: true,
                      style: {
                        width: 240,
                        flex: 0,
                        marginLeft: 80,
                        paddingRight: 24,
                      },
                    },
                  ]}
                  rows={data?.pages[page] ? rows(data?.pages[page].data) : []}
                  page={page}
                  total={data?.pages[0].meta.totalCount}
                  rowsPerPage={10}
                  onPageChange={(p: number) => {
                    const currentPage = data?.pages.at(-1).meta.page;

                    if (p > currentPage - 1) {
                      fetchNextPage();
                    }

                    setPage(p);
                  }}
                  style={{ marginBottom: 100 }}
                />
              )
            ) : (
              <FlashList
                data={payouts}
                renderItem={({ item }) => (
                  <Pressable
                    onPress={() => {
                      router.push(`/partner/payouts/${item.id}`);
                    }}
                  >
                    <Card style={{ width: '100%', padding: 16, gap: 16 }}>
                      <View>
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Payout Period
                        </Text>
                        <Text type="title2">
                          {`${formatInUTC(
                            item.startTime,
                            'MMM d, yyyy'
                          )} - ${formatInUTC(item.endTime, 'MMM d, yyyy')}`}
                        </Text>
                      </View>

                      <View>
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Net Earnings
                        </Text>
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.success }}
                        >
                          ${formatNumber(item.earningsMinusExpenses)}
                        </Text>
                      </View>

                      {item.paymentStatus ? (
                        <View>
                          <Text
                            type="caption1"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            Status
                          </Text>
                          <Text
                            type="bodyRegular"
                            style={{
                              color:
                                item.paymentStatus === 'paid'
                                  ? theme.colors.success
                                  : item.paymentStatus === 'partial_paid'
                                  ? theme.colors.chip.warning.color
                                  : item.paymentStatus === 'failed'
                                  ? theme.colors.error
                                  : item.paymentStatus === 'pending'
                                  ? theme.colors.chip.pending.color
                                  : theme.colors.text.secondary,
                            }}
                          >
                            {item.paymentStatus === 'paid' && item.paymentDate
                              ? `Paid on ${format(
                                  new Date(item.paymentDate),
                                  'MMMM d, yyyy'
                                )}`
                              : item.paymentStatus === 'partial_paid'
                              ? 'Partial paid'
                              : item.paymentStatus === 'pending'
                              ? 'Pending'
                              : item.paymentStatus === 'failed'
                              ? 'Failed'
                              : 'Unpaid'}
                          </Text>
                        </View>
                      ) : null}
                    </Card>
                  </Pressable>
                )}
                ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
                onEndReached={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    console.log('end reached');
                    fetchNextPage();
                  }
                }}
                onRefresh={async () => {
                  await refetchPayouts();
                }}
                refreshing={isLoading}
                estimatedItemSize={20}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{}}
                style={{ width: '100%' }}
                ListFooterComponent={() => {
                  if (!isFetchingNextPage) return null;

                  return <Loader style={{ height: 140 }} />;
                }}
              />
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    maxHeight: '100%',
    gap: 56,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  payoutsContainer: {
    flex: {
      xs: 1,
      md: undefined,
    },
  },
}));
